import type { WorkbookAccessMode } from "@/business/business";
import { WORKBOOK_ACCESS_MODES } from "@/business/constants";
import { isPayrollFeatureEnabled } from "@/business/product-access";
import { useGetCurrentUserWorkbookAccess } from "@/business/query";
import { useAuth } from "@/core/auth";
import { useBusiness } from "@/core/business";
import { useBusinessProducts } from "@/core/business/use-business-products";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import type { UserRole } from "@/user/user";
import { isAdmin, isManager, isOwner } from "@/user/utils";
import { PAYROLL_EMAILS_ACCESS, type PayrollEmailsAccess } from "./types";

export const getPayrollEmailsAccess = ({
  role,
  workbookAccessMode,
  canPublishPaypacks,
  showPayrollEmails,
  enablePayrollEmails,
}: {
  role: UserRole | undefined;
  workbookAccessMode?: WorkbookAccessMode;
  canPublishPaypacks?: boolean;
  showPayrollEmails: boolean;
  enablePayrollEmails: boolean;
}): PayrollEmailsAccess => {
  // Owners with team access and managers follow the API's publish permission.
  const hasRoleAccess =
    isAdmin(role) ||
    (isOwner(role) &&
      (workbookAccessMode === WORKBOOK_ACCESS_MODES.unconfigured ||
        workbookAccessMode === WORKBOOK_ACCESS_MODES.full ||
        canPublishPaypacks === true)) ||
    (isManager(role) && canPublishPaypacks === true);

  if (!hasRoleAccess) return PAYROLL_EMAILS_ACCESS.hidden;
  if (enablePayrollEmails) return PAYROLL_EMAILS_ACCESS.enabled;
  if (showPayrollEmails) return PAYROLL_EMAILS_ACCESS.preview;
  return PAYROLL_EMAILS_ACCESS.hidden;
};

export const usePayrollEmailsAccess = () => {
  const { user } = useAuth();
  const { businessId } = useBusiness();
  const needsWorkbookAccess = isOwner(user?.role) || isManager(user?.role);
  const workbookAccessQuery = useGetCurrentUserWorkbookAccess(businessId, {
    enabled: needsWorkbookAccess,
  });
  const {
    settings,
    isLoading: productsLoading,
    hasValidSettings,
  } = useBusinessProducts();
  const enablePayrollEmails = isPayrollFeatureEnabled(settings, "paypacks");
  const {
    enabled: showPayrollEmails,
    isLoading: showFlagLoading,
    isError: showFlagError,
  } = useFeatureFlag(FEATURE_FLAG_NAMES.show_payroll_emails);

  const productsUnavailable = productsLoading || !hasValidSettings;
  const previewUnavailable =
    productsUnavailable || showFlagLoading || showFlagError;

  return {
    payrollEmailsAccess: getPayrollEmailsAccess({
      role: user?.role,
      workbookAccessMode: workbookAccessQuery.isError
        ? undefined
        : workbookAccessQuery.data?.mode,
      canPublishPaypacks:
        !workbookAccessQuery.isError &&
        workbookAccessQuery.data?.can_publish_paypacks === true,
      showPayrollEmails: !previewUnavailable && showPayrollEmails,
      enablePayrollEmails: !productsUnavailable && enablePayrollEmails,
    }),
    isLoading:
      productsLoading ||
      (!enablePayrollEmails && showFlagLoading) ||
      (needsWorkbookAccess && workbookAccessQuery.isLoading),
  };
};

// PackChat and approvals use saved PayPack access, independently of the preview
// flag. Statement release timing remains controlled by the API's operational flag.
export const usePaypacksAccess = () => {
  const { settings, isLoading } = useBusinessProducts();
  return { enabled: isPayrollFeatureEnabled(settings, "paypacks"), isLoading };
};
